import qs from "qs";
import {SIDEBAR_VALS, SIDEBAR_QUERYKEY} from "./side-bar-info";
import {ASQueryButton, ASPathButton} from "../../components/ActionStripButtons";
import {setOpen} from "../../components/ShortcutLister";
import {actionButtonStyles, actionButtonStyles as styles} from "./card-panel.css";
import {cx, TooltipForChild, useGlobalKeyPress} from "@cdx/common";
import {deckHasDefaultCard} from "../workflows/workflow-utils";
import {cardCreationPersistStore} from "../card-container/CreateCard";
import {hasPermissionToCreateCard} from "../../lib/permissions";
import {cardContainerStyles} from "../card-container/unified-card-container.css";
import {
  Box,
  Col,
  DSIconAddCard,
  DSIconBarChart,
  DSIconButton,
  DSIconHistory,
  DSIconInfo,
  DSIconJourney,
  DSIconKeyboard,
  DSIconSort,
  DSIconTag,
  Text,
} from "@cdx/ds";
import {isSameOrder, useCardOrder} from "../card-order/useCardOrder";
import {getPreferredOrderFromContext} from "../card-order/sort-panel";
import messenger from "../../lib/messenger";

const AddButton = ({queryVal, shortcut, location, rootUrl, history, disabled, arenaCtx}) => {
  const showDraft = cardCreationPersistStore.useHasStoredValues(arenaCtx);

  const isInDeck = arenaCtx.type === "deck";
  const withPreset = isInDeck && deckHasDefaultCard(arenaCtx.model);
  const label = withPreset ? "Add Card using custom Deck Template" : "Add Card";

  const query = qs.parse(location.search, {ignoreQueryPrefix: true});
  const active = query[SIDEBAR_QUERYKEY] === queryVal;

  const tooltip = showDraft ? (
    <Col sp="4px" pa="8px">
      <Text type="prose12" color="primary">
        {label}
      </Text>
      <Text type="prose12" color="secondary">
        There's an unsaved draft
      </Text>
    </Col>
  ) : (
    label
  );

  return (
    <Box relative rounded={4} overflow="hidden">
      <ASQueryButton
        queryVal={queryVal}
        location={location}
        history={history}
        rootUrl={rootUrl}
        tooltip={tooltip}
        shortcut={{description: label, ...shortcut}}
        disabled={disabled}
        icon={<DSIconAddCard />}
        variant="primary"
      />
      {showDraft ? (
        <div className={actionButtonStyles.draftHint} />
      ) : withPreset && !disabled ? (
        <div className={cx(styles.withPreset.main.base, active && styles.withPreset.main.active)}>
          <DSIconJourney
            size={16}
            className={cx(styles.withPreset.icon.base, active && styles.withPreset.icon.active)}
          />
        </div>
      ) : null}
    </Box>
  );
};

export const AddCardButton = ({arenaCtx, location, rootUrl, history, disabled, category}) => {
  useGlobalKeyPress({
    key: "n",
    fn: () => {
      messenger.send(`"n" has been deprecated for creating a Card. Please use "c" instead.`);
      const query = qs.parse(location.search, {ignoreQueryPrefix: true});
      history.replace({
        pathname: rootUrl || "/",
        search: qs.stringify({...query, [SIDEBAR_QUERYKEY]: SIDEBAR_VALS.ADD_CARD}),
      });
    },
    disabled,
  });
  return (
    <AddButton
      queryVal={SIDEBAR_VALS.ADD_CARD}
      shortcut={{key: "c", description: "Add Card", category}}
      location={location}
      rootUrl={rootUrl}
      history={history}
      disabled={disabled}
      arenaCtx={arenaCtx}
    />
  );
};

export const ShortcutHelp = () => (
  <Col align="center" py="12px">
    <TooltipForChild tooltip="See currently available shortcuts">
      <DSIconButton
        icon={<DSIconKeyboard />}
        variant="tertiary"
        onClick={() => setOpen(true)}
        size="md"
        negatePadding
      />
    </TooltipForChild>
  </Col>
);

const showAddCardButton = ({arenaCtx, root}) => {
  if (arenaCtx.type === "library") return false;
  if (arenaCtx.type === "dueDate") return false;
  if (!hasPermissionToCreateCard(root)) return false;
  if (arenaCtx.type === "deck" && arenaCtx.model.isDeleted) return false;
  return true;
};

const FallbackSidebarButton = ({showSidebarFallback, setShowSidebarFallback, arenaCtx}) => {
  const hasDesc = () => Boolean(arenaCtx.model?.description);
  return (
    <TooltipForChild tooltip="Show description and statistics if no card is open" placement="right">
      <DSIconButton
        active={showSidebarFallback}
        customSize="actionButton"
        variant="secondary"
        onClick={() => setShowSidebarFallback((v) => !v)}
        className={!showSidebarFallback && hasDesc() && actionButtonStyles.sidebarFallback.withDesc}
        icon={<DSIconInfo />}
      />
    </TooltipForChild>
  );
};

const ActionButtons = ({
  cards,
  arenaCtx,
  root,
  location,
  history,
  withShortcutHelp,
  isGlobalSearch,
  supportsFallbackSidebar,
  showSidebarFallback,
  setShowSidebarFallback,
}) => {
  const {getRootUrl, rootPath} = arenaCtx.routes;
  const rootUrl = getRootUrl();
  const [order] = useCardOrder();
  const isNonPreferredOrderActive = () => {
    const info = getPreferredOrderFromContext(arenaCtx, root);
    if (!info || !info.preferredOrder) return false;
    return !isSameOrder(order, info.preferredOrder);
  };
  const nonPrefOrderActive = isNonPreferredOrderActive();
  return (
    <Col>
      <Col flex="auto">
        <Col sp="12px" position="sticky" className={cardContainerStyles.stickyTop} pt="12px">
          {process.env.REACT_APP_MODE !== "open" && showAddCardButton({arenaCtx, root}) && (
            <AddCardButton
              arenaCtx={arenaCtx}
              location={location}
              history={history}
              rootUrl={rootUrl}
              disabled={!cards}
              category={arenaCtx.shortcutCategory}
            />
          )}
          {supportsFallbackSidebar && (
            <FallbackSidebarButton
              showSidebarFallback={showSidebarFallback}
              setShowSidebarFallback={setShowSidebarFallback}
              arenaCtx={arenaCtx}
            />
          )}
          <Box relative>
            <ASQueryButton
              queryVal={SIDEBAR_VALS.SORT}
              location={location}
              history={history}
              rootUrl={rootUrl}
              tooltip={nonPrefOrderActive ? "Preferred order not active" : "Change card ordering"}
              shortcut={{
                code: "KeyO",
                withShift: true,
                category: arenaCtx.shortcutCategory,
                description: "Open order panel",
              }}
              disabled={!cards}
              icon={<DSIconSort />}
            />
            {nonPrefOrderActive && <div className={styles.orderHint} />}
          </Box>
          {process.env.REACT_APP_MODE !== "open" && (
            <ASQueryButton
              queryVal={SIDEBAR_VALS.TAGS}
              location={location}
              history={history}
              rootUrl={rootUrl}
              tooltip="Display and select by tags"
              shortcut={{
                code: "KeyT",
                withShift: true,
                category: arenaCtx.shortcutCategory,
                description: "Open tag panel",
              }}
              icon={<DSIconTag />}
              disabled={!cards}
            />
          )}
          {process.env.REACT_APP_MODE !== "open" &&
            !isGlobalSearch &&
            arenaCtx.type !== "dueDate" &&
            arenaCtx.type !== "sprint" && (
              <ASPathButton
                pathSegement="metrics"
                rootPath={rootPath}
                rootUrl={rootUrl}
                location={location}
                history={history}
                tooltip="Show metrics"
                shortcut={{
                  code: "KeyM",
                  withShift: true,
                  category: arenaCtx.shortcutCategory,
                  description: "Open metrics",
                }}
                icon={<DSIconBarChart />}
              />
            )}
          {arenaCtx.model && "activities" in arenaCtx.model && (
            <ASPathButton
              pathSegement="activity"
              rootPath={rootPath}
              rootUrl={rootUrl}
              location={location}
              history={history}
              tooltip="Browse recent activity"
              shortcut={{
                code: "KeyI",
                withShift: true,
                category: arenaCtx.shortcutCategory,
                description: "Open activity panel",
              }}
              icon={<DSIconHistory />}
            />
          )}
        </Col>
      </Col>
      {withShortcutHelp && <ShortcutHelp />}
    </Col>
  );
};

export default ActionButtons;
